import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React from "react";
import Dropdown from "react-bootstrap/Dropdown";
import DropdownButton from "react-bootstrap/DropdownButton";
import CustomPagination from "Shared/JsxPagination";
import SearchDatePicker from "Shared/Common/DatePicker/SearchDatePicker";
import PermissionComponent from "Shared/Common/Permissions/PermissionComponent";
import {
  CREATE_RUNS_PERMISSION,
  DELETE_RUN_PERMISSION,
  ID_LIS_MODULE_NAME,
  VIEW_CREATE_RUN_PERMISSION_PAGE,
  type RunGridRow,
} from "../viewCreateRunHelpers";
import type { IdLisRunSearchFilters } from "Services/InfectiousDisease/IdLisRunService";

type TranslationFn = (value: string) => string;

type RunTabContentProps = {
  tabIndex: number;
  t: TranslationFn;
  pageSize: number;
  setPageSize: (value: number) => void;
  setCurPage: (value: number) => void;
  showAddPanel: boolean;
  onToggleAddPanel: () => void;
  onSearchRuns: () => void;
  onReset: () => void;
  isRunsLookupError: boolean;
  runsLookupError: any;
  runsListError: string | null;
  searchedTags: string[];
  onTagRemoval: (tag: string) => void;
  search: IdLisRunSearchFilters;
  setSearch: React.Dispatch<React.SetStateAction<IdLisRunSearchFilters>>;
  runTypeOptions: string[];
  runTemplateOptions: string[];
  renderSortHeader: (label: string, column: string) => React.ReactNode;
  isRunsLoading: boolean;
  runRows: RunGridRow[];
  expandedId: string | null;
  setExpandedId: React.Dispatch<React.SetStateAction<string | null>>;
  onDeleteRun: (row: RunGridRow) => void;
  isDeletingRun: boolean;
  renderRunDetail: (row: RunGridRow) => React.ReactNode;
  curPage: number;
  nextPage: () => void;
  pageNumbers: number[];
  prevPage: () => void;
  showPage: (value: number) => void;
  total: number;
  totalPages: number;
};

export type RunTabBaseProps = Omit<RunTabContentProps, "tabIndex">;

const RunTabContent: React.FC<RunTabContentProps> = ({
  tabIndex,
  t,
  pageSize,
  setPageSize,
  setCurPage,
  showAddPanel,
  onToggleAddPanel,
  onSearchRuns,
  onReset,
  isRunsLookupError,
  runsLookupError,
  runsListError,
  searchedTags,
  onTagRemoval,
  search,
  setSearch,
  runTypeOptions,
  runTemplateOptions,
  renderSortHeader,
  isRunsLoading,
  runRows,
  expandedId,
  setExpandedId,
  onDeleteRun,
  isDeletingRun,
  renderRunDetail,
  curPage,
  nextPage,
  pageNumbers,
  prevPage,
  showPage,
  total,
  totalPages,
}) => {
  return (
    <>
      <div className="responsive-flexed-actions gap-2 mb-2 d-flex flex-wrap justify-content-center justify-content-sm-between align-items-center">
        <div className="d-flex align-items-center gap-2 responsive-flexed-actions">
          <div className="d-flex align-items-center">
            <span className="fw-400 mr-3">{t("Records")}</span>
            <select
              id="IDViewCreateRunPageSize"
              className="form-select w-125px h-33px rounded py-2"
              value={pageSize}
              onChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setCurPage(1);
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          {tabIndex === 0 && (
            <PermissionComponent
              moduleName={ID_LIS_MODULE_NAME}
              pageName={VIEW_CREATE_RUN_PERMISSION_PAGE}
              permissionIdentifier={CREATE_RUNS_PERMISSION}
            >
              <button
                id="IDViewCreateRunCreateRuns"
                type="button"
                className="btn btn-sm btn-primary px-7"
                onClick={onToggleAddPanel}
                aria-expanded={showAddPanel}
              >
                <i style={{ fontSize: "15px" }} className="fa">
                  &#xf067;
                </i>
                <span> {t("Create Runs")} </span>
              </button>
            </PermissionComponent>
          )}
        </div>
        <div className="d-flex align-items-center gap-2 gap-lg-3">
          <button
            id="IDViewCreateRunSearch"
            type="button"
            className="btn btn-linkedin btn-sm fw-500"
            onClick={onSearchRuns}
          >
            {t("Search")}
          </button>
          <button
            id="IDViewCreateRunReset"
            type="button"
            className="btn btn-secondary btn-sm btn-secondary--icon fw-500"
            onClick={onReset}
          >
            <span>
              <span>{t("Reset")}</span>
            </span>
          </button>
        </div>
      </div>

      {isRunsLookupError && (
        <div className="text-danger small mb-3">
          {t(
            runsLookupError?.response?.data?.message ||
              runsLookupError?.message ||
              "Unable to load run filter options"
          )}
        </div>
      )}

      {runsListError && (
        <div className="text-danger small mb-3">{runsListError}</div>
      )}

      <div className="d-flex gap-2 flex-wrap pb-2">
        {searchedTags.map((tag) => (
          <div
            key={tag}
            className="d-flex align-items-center cursor-pointer gap-1 p-2 rounded bg-light"
            onClick={() => onTagRemoval(tag)}
          >
            <span className="fw-bold">{tag}</span>
            <i className="bi bi-x" />
          </div>
        ))}
      </div>

      <Box sx={{ height: "auto", width: "100%" }}>
        <div className="table_bordered overflow-hidden">
          <TableContainer
            sx={{
              maxHeight: "calc(100vh - 100px)",
              "&::-webkit-scrollbar": { width: 7 },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#fff",
              },
              "&:hover": {
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "var(--kt-gray-400)",
                  borderRadius: 2,
                },
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "var(--kt-gray-400)",
                borderRadius: 2,
              },
            }}
            component={Paper}
            className="shadow-none"
          >
            <Table
              aria-label="id view create run"
              className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
            >
              <TableHead>
                <TableRow className="h-40px">
                  <TableCell />
                  <TableCell />
                  <TableCell>
                    <input
                      id="IDViewCreateRunSearchRunId"
                      type="text"
                      className="form-control bg-white min-w-100px w-100 rounded-2 fs-8 h-30px"
                      placeholder={t("Search...")}
                      value={search.runId}
                      onChange={(e) =>
                        setSearch((p) => ({
                          ...p,
                          runId: e.target.value,
                        }))
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <input
                      id="IDViewCreateRunSearchRunName"
                      type="text"
                      className="form-control bg-white min-w-100px w-100 rounded-2 fs-8 h-30px"
                      placeholder={t("Search...")}
                      value={search.runName}
                      onChange={(e) =>
                        setSearch((p) => ({
                          ...p,
                          runName: e.target.value,
                        }))
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <select
                      id="IDViewCreateRunSearchRunType"
                      className="form-select bg-white min-w-100px w-100 rounded-2 fs-8 h-30px py-0"
                      value={search.runType}
                      onChange={(e) =>
                        setSearch((p) => ({
                          ...p,
                          runType: e.target.value,
                        }))
                      }
                    >
                      <option value="">{t("Select")}</option>
                      {runTypeOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </TableCell>
                  <TableCell>
                    <select
                      id="IDViewCreateRunSearchRunTemplate"
                      className="form-select bg-white min-w-100px w-100 rounded-2 fs-8 h-30px py-0"
                      value={search.runTemplate}
                      onChange={(e) =>
                        setSearch((p) => ({
                          ...p,
                          runTemplate: e.target.value,
                        }))
                      }
                    >
                      <option value="">{t("Select")}</option>
                      {runTemplateOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </TableCell>
                  <TableCell>
                    <SearchDatePicker
                      name="IDViewCreateRunSearchRunDate"
                      customClass="search-date-picker"
                      value={search.runDate}
                      onChange={(e: any) =>
                        setSearch((p) => ({
                          ...p,
                          runDate: e?.target?.value ?? "",
                        }))
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <input
                      id="IDViewCreateRunSearchAddedBy"
                      type="text"
                      className="form-control bg-white min-w-100px w-100 rounded-2 fs-8 h-30px"
                      placeholder={t("Search...")}
                      value={search.addedBy}
                      onChange={(e) =>
                        setSearch((p) => ({
                          ...p,
                          addedBy: e.target.value,
                        }))
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <input
                      id="IDViewCreateRunSearchBarcodeNo"
                      type="text"
                      className="form-control bg-white min-w-100px w-100 rounded-2 fs-8 h-30px"
                      placeholder={t("Search...")}
                      value={search.plateBarcodeNo}
                      onChange={(e) =>
                        setSearch((p) => ({
                          ...p,
                          plateBarcodeNo: e.target.value,
                        }))
                      }
                    />
                  </TableCell>
                </TableRow>

                <TableRow className="h-30px">
                  <TableCell
                    className="min-w-50px"
                    style={{ width: "49px" }}
                  />
                  <TableCell
                    className="min-w-50px"
                    style={{ width: "49px" }}
                  >
                    {t("Actions")}
                  </TableCell>
                  <TableCell className="min-w-100px">
                    {renderSortHeader("Run ID", "id")}
                  </TableCell>
                  <TableCell className="min-w-120px">
                    {renderSortHeader("Run Name", "runName")}
                  </TableCell>
                  <TableCell className="min-w-150px">
                    {renderSortHeader("Run Type", "runType")}
                  </TableCell>
                  <TableCell className="min-w-100px">
                    {renderSortHeader("Run Template", "runTemplate")}
                  </TableCell>
                  <TableCell className="min-w-100px">
                    {renderSortHeader("Run Date", "runDate")}
                  </TableCell>
                  <TableCell className="min-w-130px">
                    {renderSortHeader("Added By", "addedBy")}
                  </TableCell>
                  <TableCell className="min-w-140px">
                    {renderSortHeader(
                      "Plate Barcode No",
                      "plateBarcodeNo"
                    )}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody
                sx={{
                  "& .MuiTableCell-body": {
                    padding: "6px 16px !important",
                    fontWeight: 400,
                    fontSize: "14px !important",
                    lineHeight: "18px",
                    borderBottom: 0,
                    color: "#3F4254",
                    verticalAlign: "middle",
                  },
                }}
              >
                {isRunsLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      className="text-center py-4"
                    >
                      {t("Loading...")}
                    </TableCell>
                  </TableRow>
                ) : runRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      className="text-center py-4"
                    >
                      {t("No records found")}
                    </TableCell>
                  </TableRow>
                ) : (
                  runRows.map((row) => {
                    const canDeleteRun = tabIndex === 0 || tabIndex === 1;

                    return (
                      <React.Fragment key={row.id}>
                        <TableRow className="h-30px">
                          <TableCell>
                            {expandedId === row.id ? (
                              <button
                                type="button"
                                id={`IDViewCreateRunClose_${row.id}`}
                                className="btn btn-icon btn-icon-light btn-sm fw-bold btn-table-expend-row rounded h-20px w-20px min-h-20px"
                                onClick={() => setExpandedId(null)}
                                title={t("Collapse")}
                              >
                                <i className="bi bi-dash-lg" />
                              </button>
                            ) : (
                              <button
                                type="button"
                                id={`IDViewCreateRunOpen_${row.id}`}
                                className="btn btn-icon btn-icon-light btn-sm fw-bold btn-primary rounded h-20px w-20px min-h-20px"
                                onClick={() => setExpandedId(row.id)}
                                title={t("Expand")}
                              >
                                <i className="bi bi-plus-lg" />
                              </button>
                            )}
                          </TableCell>

                          <TableCell>
                            <div className="d-flex justify-content-center rotatebtnn">
                              {canDeleteRun && (
                                <DropdownButton
                                  className="p-0 del-before btn btn-light-info btn-active-info btn-sm btn-action table-action-btn"
                                  id={`IDViewCreateRunMenu_${row.id}`}
                                  drop="end"
                                  title={
                                    <i className="bi bi-three-dots-vertical p-0" />
                                  }
                                >
                                  <PermissionComponent
                                    moduleName={ID_LIS_MODULE_NAME}
                                    pageName={VIEW_CREATE_RUN_PERMISSION_PAGE}
                                    permissionIdentifier={DELETE_RUN_PERMISSION}
                                  >
                                    <Dropdown.Item
                                      className="menu-item p-0"
                                      onClick={() => onDeleteRun(row)}
                                      disabled={isDeletingRun}
                                    >
                                      <span className="menu-link text-dark">
                                        <i className="fa fa-trash text-danger mr-2 w-20px" />
                                        {isDeletingRun
                                          ? t("Deleting...")
                                          : t("Delete")}
                                      </span>
                                    </Dropdown.Item>
                                  </PermissionComponent>
                                </DropdownButton>
                              )}
                            </div>
                          </TableCell>

                          <TableCell>{row.id}</TableCell>
                          <TableCell>{row.runName}</TableCell>
                          <TableCell>{row.runType}</TableCell>
                          <TableCell>{row.runTemplate}</TableCell>
                          <TableCell>{row.runDate}</TableCell>
                          <TableCell>{row.addedBy}</TableCell>
                          <TableCell>{row.plateBarcodeNo}</TableCell>
                        </TableRow>

                        {expandedId === row.id && (
                          <TableRow>
                            <TableCell
                              colSpan={9}
                              className="p-0 border-0"
                            >
                              {renderRunDetail(row)}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </Box>

      {total > 0 && (
        <CustomPagination
          curPage={curPage}
          nextPage={nextPage}
          pageNumbers={pageNumbers}
          pageSize={pageSize}
          prevPage={prevPage}
          showPage={showPage}
          total={total}
          totalPages={totalPages}
        />
      )}
    </>
  );
};

export default RunTabContent;
